/* Presentation header */
import React from "react";
import { Link } from "react-router-dom";
import l from "components/util/l";
import HelpMessage from 'components/util/helpers/HelpMessage';
import HeaderItem from "components/presentation/Presentation.sub.HeaderItem.view";
import Environments from "components/presentation/environments/Environments";
import ProfileIcon from "components/util/helpers/icons/ProfileIcon";
import { VIEW_ICONS, VIEW_LABELS, VIEW_ORDERS, canShowView } from "components/util/views";
import { usePermissions } from "components/util/helpers/providers/PermissionsProvider";
import { HasPermission } from "components/util/helpers/HasPermission";
import { PERMISSIONS } from "components/util/permissions";
import "styles/css/presentation/Presentation.css";

const PresentationHeader = ({
  user,
  flow,
  buildStatus,
  presentationSettings,
  activeSection,
  isAdmin,
  isDeveloper,
  isEmbedded,
  isPinnedToFlow,
  onClickStartButton,
  onClickTab,
  onClickPinView,
  onClickShowAssistant,
  onClickShowContactUs
}) => {

  const { hasPermission } = usePermissions();

  return <div id="presentation-header"
    className={"d-flex w-100 justify-content-between align-items-center"}>

    <div id="presentation-title" class="d-flex align-items-center">

      {/* Start button, only on general access — not pinned to one flow, not anonymous */}
      {!isPinnedToFlow && !user?.isAnonymous &&
        <div className="start-button" onClick={onClickStartButton}>
          <i class="tio-apps d-block "></i>
        </div>}

      {/* Logo */}
      <div class="w-auto d-flex align-items-center">
        <a href="/">
          <div className={"logo " +
            (flow.theme.logo ? "custom " : " ") +
            (user?.isAnonymous ? "ml-2 " : " ")}
            style={function () {
              let style = {}
              if (flow.theme.logo) style = { backgroundImage: `url(${flow.theme.logo})`, ...style }
              if (flow.theme.logoStyle) style = { ...flow.theme.logoStyle, ...style }
              return style
            }()}></div>
        </a>
      </div>

      {/* Flow title with edit button */}
      <div className="my-0">
        <span className="flow-title-container d-flex align-items-center text-hover-primary">
          <Link className="h2 flow-title d-flex align-items-center mb-0"
            to={!isPinnedToFlow && !user?.isAnonymous ? `/edit_flow` : "/"}>
            <div className="flow-name d-flex align-items-center">
              <span className="text-truncate">{flow.name}</span>
              <div className="dev-badge"><label>Dev</label></div>
            </div>
            {!isPinnedToFlow && !user?.isAnonymous &&
              <div class="py-1 flow-edit-btn rounded">
                <i class="fa-solid fa-pen"></i>
              </div>}
          </Link>
        </span>
      </div>

    </div>

    {/* Menu with views only if flow is built or prototype, or user is developer */}
    {(["BUILT", "PROTOTYPE", "TEMPLATE"].includes(buildStatus) || isDeveloper) &&
      <div id="presentation-menu"
        className="d-flex align-items-center">

        {function () {

          const filteredViews = (presentationSettings?.views ?? [])
            // The project's app is not a tab: it is what the "main" section renders.
            .filter((view) => view.is_prebuilt)
            .filter(v => !v.is_hidden || (v.name == "settings" && isAdmin))
            // Anonymous users only ever reached "agent" and "history", which are no longer tabs
            .filter(() => !user?.isAnonymous)
            .filter((view) => canShowView(view, isDeveloper, hasPermission))
            .sort((a, b) => {
              const indexA = VIEW_ORDERS.indexOf(a.name);
              const indexB = VIEW_ORDERS.indexOf(b.name);
              if (indexA === -1 && indexB === -1) return 0;
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });

          const isPrimary = (name) =>
            name == "main" || name == "brief";

          // Keeps the VIEW_ORDERS order already applied to filteredViews, so a pinned
          // view renders in its declared position and not at the end.
          const pinnedViews = filteredViews
            .filter(v => v.is_pinned || v.name == activeSection.name || isPrimary(v.name))

          const dropdownViews = filteredViews
            .filter(v => !isPrimary(v.name))
            .filter(v => !pinnedViews.includes(v))
            .sort((a, b) => {
              if (a.is_pinned && !b.is_pinned) return 1;
              if (!a.is_pinned && b.is_pinned) return -1;
              return 0;
            })

          if (pinnedViews.length == 1 && dropdownViews.length == 0) return null;

          return <>

            {/* Pinned views in the header */}
            {pinnedViews.map((view, index, array) => (
              <div key={view.name}
                className="d-none d-sm-flex align-items-center">
                <HeaderItem
                  name={view.name}
                  label={VIEW_LABELS[view.name] || view.label}
                  icon={VIEW_ICONS[view.name] || view.icon || "fas fa-window-maximize"}
                  isPinned={true}
                  activeSection={activeSection}
                  onClickItem={onClickTab} />
              </div>
            ))}

            {/* Dropdown for unpinned views */}
            {dropdownViews.length > 0 &&
              <div className="dropdown">
                <button
                  id="unpinned-views-dropdown-button"
                  className="btn menu-item p-0 pinned d-none d-sm-flex align-items-center"
                  type="button"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <i className="fas fa-code" />
                </button>
                <div
                  className="menu-sub dropdown-menu p-0 mt-2"
                  aria-labelledby="unpinned-views-dropdown-button"
                >
                  {dropdownViews.map((view) => (
                    <div
                      key={view.name}
                      className="menu-sub-item dropdown-item p-0"
                      role="button"
                    >
                      <HeaderItem
                        name={view.name}
                                    label={VIEW_LABELS[view.name] || view.label}
                        icon={VIEW_ICONS[view.name] || view.icon || "fas fa-window-maximize"}
                        isDropdownItem={true}
                        isPinned={view.is_pinned}
                        activeSection={activeSection}
                        onClickItem={onClickTab}
                        onClickPinView={onClickPinView}
                      />
                    </div>
                  ))}
                </div>
              </div>
            }
          </>;

        }()}

      </div>
    }

    {/* Right options: notifications, share, profile */}
    <div id="presentation-options" className="d-flex">

      <span className="d-flex align-items-center">

        {/* Environment selector and merge */}
        <HasPermission permission={PERMISSIONS.SHOW_ENVIRONMENTS} fallback={null}>
          {!user.isAnonymous && <Environments flow={flow} />}
        </HasPermission>

        {/* Share button */}
        <HasPermission permission={PERMISSIONS.SHOW_SHARE} fallback={null}>
          {!user.isAnonymous &&
            (["BUILT"].includes(buildStatus) || isDeveloper) &&
            <HelpMessage message={l("Comparte tu solución con otras personas para colaborar%PA100")}
              style={{ marginTop: "-5px", maxWidth: "14rem" }}>
              <Link to="/share"
                className="btn btn-sm btn-white option-button mr-2 font-weight-bold d-none d-md-block">
                <i class="fas fa-user-plus mx-1"></i>
              </Link>
            </HelpMessage>}
        </HasPermission>

        {/* Create agent button for TEMPLATE flows */}
        {buildStatus == "TEMPLATE_STARTED" &&
          <button
            type="button"
            className="btn btn-sm btn-accent option-button px-2 px-sm-3 mr-2 font-weight-bold d-none d-md-block"
            onClick={onClickShowContactUs}
          >
            <i class="fas fa-agent mr-1"></i> {l("Contáctanos")}
          </button>}

        {/* Profile dropdown */}
        {!user.isAnonymous &&
          <div class="dropdown">
            <div className="profile-button dropdown-toggle d-none"
              data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              <ProfileIcon style={{ width: "100%", height: "100%" }} />
            </div>

            <div class="dropdown-menu pr-1">

              <div class="dropdown-item">
                <Link to={"/settings/account"} class="d-flex align-items-center">
                  <div class="avatar avatar-sm avatar-circle mr-3">
                    <ProfileIcon style={{ width: "100%", height: "100%" }} />
                  </div>
                  <div class="flex-grow-1 ms-3">
                    <h5 class="mb-0">{user?.firstName ?? ""} {user?.lastName ?? ""}</h5>
                  </div>
                </Link>

              </div>

              <hr className="my-2" />

              <Link to={`/settings/account`} class="dropdown-item">
                <i class="fas fa-user dropdown-item-icon mr-1"></i> {l("Cuenta%VG032")}
              </Link>
              {/* Temp hide
              <Link to={`/settings/plan`} class="dropdown-item">
                <i class="fas fa-money-bill dropdown-item-icon mr-1"></i> {l("Plan y facturación%SB081")}
              </Link>
              */}
              <li className="nav-item">
                <Link to="/organizations" className="dropdown-item">
                  <i className="fas fa-building dropdown-item-icon mr-1"></i>{l("Organización%SB017")}
                </Link>
              </li>
              <hr className="my-2" />

              {/* The dashboards span the account, but each is opened by a flow
                  permission: this project granting it is what puts the link here. */}
              {(hasPermission(PERMISSIONS.SHOW_GOVERNANCE) ||
                hasPermission(PERMISSIONS.SHOW_ACTIVITY)) && <>
                <HasPermission permission={PERMISSIONS.SHOW_GOVERNANCE} fallback={null}>
                  <Link to={`/settings/governance`} class="dropdown-item">
                    <i class="fas fa-shield-alt dropdown-item-icon mr-1"></i> {l("Gobernanza%GV001")}
                  </Link>
                </HasPermission>
                <HasPermission permission={PERMISSIONS.SHOW_ACTIVITY} fallback={null}>
                  <Link to={`/settings/activity`} class="dropdown-item">
                    <i class="fas fa-clipboard-list dropdown-item-icon mr-1"></i> {l("Actividad%GV002")}
                  </Link>
                </HasPermission>

                <hr className="my-2" />
              </>}

              <HasPermission permission={PERMISSIONS.SHOW_ASSISTANT} fallback={null}>
                <button type="button" className="dropdown-item"
                  data-testid="presentation-profile-assistant"
                  onClick={onClickShowAssistant}>
                  <i className="fas fa-question-circle dropdown-item-icon mr-1"></i> {l("Asistente%SA305")}
                </button>
              </HasPermission>
              <Link to={`/settings/customization`} class="dropdown-item">
                <i class="fas fa-cog dropdown-item-icon mr-1"></i> {l("Ajustes%GA070")}
              </Link>
              {/* The token rides along only from a frame, which is the one pin with a
                  source of its own. Sending it because the session merely *is* pinned is
                  what made `flow_token` immortal: Logout wipes the storage and then writes
                  back whatever this link handed it, so a pin re-created itself on every
                  logout and no user could ever shed one. */}
              <a href={`/logout?flow_token=${isEmbedded ? flow.token : ""}`} class="dropdown-item">
                <i class="fas fa-sign-out dropdown-item-icon mr-1"></i> {l("Cerrar sesión%SB082")}
              </a>
            </div>

          </div>
        }
      </span>

    </div>

  </div>;

}


export default PresentationHeader
